import type { AxiosInstance } from 'axios';
import authService from './auth.service';

export type PayoutStatus =
  | 'pending_execution'
  | 'submitted'
  | 'executed'
  | 'failed'
  | 'returned';

export type PayoutOrigin = 'redemption' | 'departed_share' | 'nav_error_compensation';

export interface Payout {
  id: string;
  /** Null for a payout of a share funds owes a holder who left. */
  redemption_request_id: string | null;
  /** What the payout pays: a redemption, or the result of a completed term owed to a holder who left. */
  origin: PayoutOrigin;
  user_id: string;
  client_email: string;
  product_id: string;
  product_name: string;
  funds_reference_number: string;
  net_amount: string;
  /** The management fee already deducted from `net_amount` at a full exit, when there is one. */
  management_fee_amount?: string | null;
  currency_code: string;
  status: PayoutStatus;
  client_bank_account_id: string | null;
  destination_label: string | null;
  rail_trace_number: string | null;
  submitted_by: string | null;
  submitted_at: string | null;
  executed_by: string | null;
  executed_at: string | null;
  settlement_proof_ref: string | null;
  resolution_reason: string | null;
  resolved_by: string | null;
  resolved_at: string | null;
  settled_bank_transaction_id: string | null;
  /** Optimistic-lock token — send it back as `expected_version`. */
  version: number;
  created_at: string;
  age_days: number;
  /**
   * Whether books has been told the cash left. funds' RedemptionAccepted booked a payable
   * that only the portal's PayoutExecuted clears, so an executed payout that is not
   * notified means the money moved and the ledger of record still shows it as owed.
   */
  settlement_notified: boolean;
  /** The relay gave up delivering that notification; it needs a human, not more waiting. */
  settlement_delivery_abandoned: boolean;
  /** The payee has a death notice in force: held for the estate, nothing is sent until the
   *  estate can be settled (the server refuses to send it). */
  held_for_estate?: boolean;
  /** A return after settlement waiting for a second manager, or confirmed (#314). */
  settled_return?: SettledReturn | null;
  /** Who tied the bank debit after settlement, and when (#313); null when picked at settlement. */
  debit_tied_by?: string | null;
  debit_tied_at?: string | null;
}

/** A payout tied to its bank debit after settlement, for the month-end review (#313). */
export interface LateDebitTie {
  payout_id: string;
  funds_reference_number: string;
  net_amount: string;
  currency_code: string;
  executed_at: string;
  bank_transaction_id: string;
  posted_at: string;
  debit_tied_by: string;
  debit_tied_at: string;
}

/** An executed payout the bank sent back: recorded by one manager, confirmed by another. */
export interface SettledReturn {
  id: string;
  status: 'proposed' | 'confirmed';
  bank_transaction_id: string;
  credit_posted_at: string;
  /** The bank's own reference of the return. */
  bank_return_reference: string;
  reason: string;
  proposed_by: string;
  proposed_at: string;
  decided_by: string | null;
  decided_at: string | null;
  /** Accounting applied the return (always true while it waits). */
  books_notified: boolean;
  books_delivery_abandoned: boolean;
}

/** An unmatched credit that can be an executed payout coming back. */
export interface ReturnCreditCandidate {
  bank_transaction_id: string;
  posted_at: string;
  amount: string;
  currency_code: string;
  account_label: string | null;
  rail_trace: string | null;
}

/** A bank debit that can be the money of a sent payout, for the manager to pick at settlement. */
export interface BankDebitCandidate {
  bank_transaction_id: string;
  posted_at: string;
  amount: string;
  currency_code: string;
  account_label: string | null;
  /** The identifier the payment rail gave the transfer, as the bank reported it. */
  rail_trace: string | null;
  /** It equals the identifier recorded when the payout was sent. */
  trace_matches: boolean;
  /** The statement's beneficiary is the payout's destination; null when either is unknown. */
  beneficiary_matches: boolean | null;
}

export interface PayoutListResponse {
  items: Payout[];
  total: number;
  page: number;
  size: number;
}

interface VersionResponse {
  payout_id: string;
  version: number;
}

/** Bank payout settlement queue (`/api/v1/ops/payouts`). Every mutation is a
 *  money-movement step: it takes the row's `version` and a fresh TOTP code. */
class PayoutsService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  /** `returnWaiting`: only the payouts whose return after settlement waits for a second manager. */
  async list(
    status: PayoutStatus[] = [], size = 100, returnWaiting = false, withoutDebit = false,
  ): Promise<PayoutListResponse> {
    const params = new URLSearchParams();
    for (const s of status) params.append('status', s);
    params.set('size', String(size));
    if (returnWaiting) params.set('return_waiting', 'true');
    if (withoutDebit) params.set('without_debit', 'true');
    const res = await this.api.get<PayoutListResponse>(`/api/v1/ops/payouts?${params.toString()}`);
    return res.data;
  }

  async submit(
    id: string, railTraceNumber: string, expectedVersion: number, mfaCode: string,
  ): Promise<VersionResponse> {
    const res = await this.api.post<VersionResponse>(`/api/v1/ops/payouts/${id}/submit`, {
      rail_trace_number: railTraceNumber,
      expected_version: expectedVersion,
      mfa_code: mfaCode,
    });
    return res.data;
  }

  /** The imported debits that can be this sent payout's money. Manager only. */
  async bankCandidates(id: string): Promise<BankDebitCandidate[]> {
    const res = await this.api.get<BankDebitCandidate[]>(`/api/v1/ops/payouts/${id}/bank-candidates`);
    return res.data;
  }

  /** `bankTransactionId`: the debit the manager picked from `bankCandidates` as this transfer. */
  async execute(
    id: string, settlementProofRef: string, expectedVersion: number, mfaCode: string,
    bankTransactionId: string | null = null,
  ): Promise<VersionResponse> {
    const res = await this.api.post<VersionResponse>(`/api/v1/ops/payouts/${id}/execute`, {
      settlement_proof_ref: settlementProofRef,
      expected_version: expectedVersion,
      mfa_code: mfaCode,
      bank_transaction_id: bankTransactionId,
    });
    return res.data;
  }

  async fail(id: string, reason: string, expectedVersion: number, mfaCode: string): Promise<VersionResponse> {
    const res = await this.api.post<VersionResponse>(`/api/v1/ops/payouts/${id}/fail`, {
      reason, expected_version: expectedVersion, mfa_code: mfaCode,
    });
    return res.data;
  }

  async markReturned(
    id: string, reason: string, expectedVersion: number, mfaCode: string,
  ): Promise<VersionResponse> {
    const res = await this.api.post<VersionResponse>(`/api/v1/ops/payouts/${id}/return`, {
      reason, expected_version: expectedVersion, mfa_code: mfaCode,
    });
    return res.data;
  }

  /** The unmatched credits that can be this executed payout coming back. Manager only. */
  async returnCredits(id: string): Promise<ReturnCreditCandidate[]> {
    const res = await this.api.get<ReturnCreditCandidate[]>(`/api/v1/ops/payouts/${id}/return-credits`);
    return res.data;
  }

  /** A manager records that an executed payout came back; a different manager confirms. */
  async proposeSettledReturn(
    id: string, bankTransactionId: string, bankReturnReference: string, reason: string, mfaCode: string,
  ): Promise<{ settled_return_id: string }> {
    const res = await this.api.post<{ settled_return_id: string }>(
      `/api/v1/ops/payouts/${id}/settled-return`,
      {
        bank_transaction_id: bankTransactionId,
        bank_return_reference: bankReturnReference,
        reason,
        mfa_code: mfaCode,
      },
    );
    return res.data;
  }

  async confirmSettledReturn(id: string, expectedVersion: number, mfaCode: string): Promise<VersionResponse> {
    const res = await this.api.post<VersionResponse>(`/api/v1/ops/payouts/${id}/settled-return/confirm`, {
      expected_version: expectedVersion, mfa_code: mfaCode,
    });
    return res.data;
  }

  async declineSettledReturn(id: string, note: string): Promise<void> {
    await this.api.post(`/api/v1/ops/payouts/${id}/settled-return/decline`, { note });
  }

  /** A manager ties a settled payout to the debit that is its transfer (picked from `bankCandidates`). */
  async tieDebit(
    id: string, bankTransactionId: string, expectedVersion: number, mfaCode: string,
  ): Promise<VersionResponse> {
    const res = await this.api.post<VersionResponse>(`/api/v1/ops/payouts/${id}/tie-debit`, {
      bank_transaction_id: bankTransactionId, expected_version: expectedVersion, mfa_code: mfaCode,
    });
    return res.data;
  }

  /** The debits tied to a payout after settlement in the last `days` days. Manager only. */
  async lateDebitTies(days = 62): Promise<LateDebitTie[]> {
    const res = await this.api.get<LateDebitTie[]>(`/api/v1/ops/payouts/debit-ties?days=${days}`);
    return res.data;
  }

  async reissue(id: string, mfaCode: string): Promise<{ payout_id: string }> {
    const res = await this.api.post<{ payout_id: string }>(`/api/v1/ops/payouts/${id}/reissue`, {
      mfa_code: mfaCode,
    });
    return res.data;
  }
}

export default new PayoutsService();
